import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Sparkles, Download, RefreshCw, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PfpMaker = () => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [originalUrl, setOriginalUrl] = useState(null);
  const [resultDataUrl, setResultDataUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [mode, setMode] = useState("retardify"); // "hat" | "retardify"

  const onPick = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("that's not a picture, retard. (png/jpg please)");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("too chonky. max 8mb please.");
      return;
    }
    setFile(f);
    setResultDataUrl(null);
    setOriginalUrl(URL.createObjectURL(f));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    onPick(f);
  };

  const reset = () => {
    setFile(null);
    setOriginalUrl(null);
    setResultDataUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const generate = async () => {
    if (!file) {
      toast("upload a pic first 👀");
      return;
    }
    setLoading(true);
    setResultDataUrl(null);
    const tId = toast.loading(
      mode === "hat" ? "fitting the propeller hat 🪖" : "cooking the full retard 🎨",
      { description: "this takes 15–45 seconds. don't refresh." }
    );
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("mode", mode);
      const res = await axios.post(`${API}/retardify`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const { image_b64, mime_type } = res.data;
      setResultDataUrl(`data:${mime_type};base64,${image_b64}`);
      toast.success("you have been retardified ✨", { id: tId });
    } catch (err) {
      let msg;
      if (err?.code === "ECONNABORTED") {
        msg = "the ai took too long 😴 hit retardify again — usually works next try.";
      } else if (err?.message === "Network Error" || !err?.response) {
        msg = "connection dropped mid-cook. give it a sec and try again.";
      } else {
        msg = err?.response?.data?.detail || err?.message || "something broke. blame the propeller.";
      }
      toast.error(msg, { id: tId });
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!resultDataUrl) return;
    const a = document.createElement("a");
    a.href = resultDataUrl;
    a.download = "retardified-pfp.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("saved! now use it as your pfp you legend 🪖");
  };

  return (
    <section id="pfp-maker" className="px-5 md:px-10 py-14" data-testid="pfp-maker-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <p className="font-display text-2xl md:text-3xl text-[#E8742C]">✦ free tool ✦</p>
          <h2 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95]">
            pfp <span className="text-[#E8742C]">retard</span> maker
          </h2>
          <p className="text-lg md:text-xl text-[#1A1A1A]/70 mt-2">
            upload your face. pick a mode. become $RETARD.
          </p>
        </div>

        {/* Mode toggle */}
        <div
          className="max-w-2xl mx-auto mb-8 grid grid-cols-1 sm:grid-cols-2 gap-4"
          data-testid="pfp-mode-toggle"
        >
          <button
            type="button"
            onClick={() => setMode("hat")}
            className={`crayon-card-soft p-4 text-left transition-all ${
              mode === "hat" ? "ring-4 ring-[#E8742C] -translate-y-1" : "opacity-75 hover:opacity-100"
            }`}
            data-testid="pfp-mode-hat"
          >
            <div className="font-display text-2xl md:text-3xl">🪖 just the hat</div>
            <div className="text-sm md:text-base text-[#1A1A1A]/70">
              slap the propeller hat on your pic. keeps your face exactly as is.
            </div>
          </button>
          <button
            type="button"
            onClick={() => setMode("retardify")}
            className={`crayon-card-soft p-4 text-left transition-all ${
              mode === "retardify"
                ? "ring-4 ring-[#E8742C] -translate-y-1"
                : "opacity-75 hover:opacity-100"
            }`}
            data-testid="pfp-mode-retardify"
          >
            <div className="font-display text-2xl md:text-3xl">🎨 full retardify</div>
            <div className="text-sm md:text-base text-[#1A1A1A]/70">
              redraw you as the $RETARD mascot character. comic cartoon style.
            </div>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* LEFT: upload + original */}
          <div className="crayon-card relative p-5 md:p-7 -rotate-1">
            <span className="tape" />
            <h3 className="font-display text-3xl md:text-4xl mb-3 text-center">
              step 1 — your pic
            </h3>

            {!originalUrl ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`w-full aspect-square border-4 border-dashed border-[#1A1A1A] rounded-2xl flex flex-col items-center justify-center gap-3 transition-colors ${
                  dragOver ? "bg-[#F4A261]/30" : "bg-[#FFF5E5] hover:bg-[#FFE9C7]"
                }`}
                data-testid="pfp-upload-dropzone"
              >
                <Upload className="w-14 h-14 text-[#E8742C]" />
                <div className="font-display text-3xl md:text-4xl">drop pic here</div>
                <div className="text-base text-[#1A1A1A]/70">
                  or click to pick (png / jpg, max 8mb)
                </div>
              </button>
            ) : (
              <div className="space-y-3">
                <div className="w-full aspect-square border-4 border-[#1A1A1A] rounded-2xl overflow-hidden bg-[#FFF5E5]">
                  <img
                    src={originalUrl}
                    alt="your upload"
                    className="w-full h-full object-cover"
                    data-testid="pfp-original-preview"
                  />
                </div>
                <button
                  type="button"
                  onClick={reset}
                  className="crayon-btn crayon-btn--ghost w-full text-xl"
                  data-testid="pfp-reset-button"
                >
                  <RefreshCw className="w-5 h-5" /> pick a different pic
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              className="hidden"
              onChange={(e) => onPick(e.target.files?.[0])}
              data-testid="pfp-file-input"
            />
          </div>

          {/* RIGHT: result */}
          <div className="crayon-card relative p-5 md:p-7 rotate-1">
            <span className="tape" style={{ background: "rgba(42, 157, 143, 0.55)" }} />
            <h3 className="font-display text-3xl md:text-4xl mb-3 text-center">
              step 2 — your retard
            </h3>

            <div className="w-full aspect-square border-4 border-[#1A1A1A] rounded-2xl overflow-hidden bg-[#FFF5E5] flex items-center justify-center relative">
              {resultDataUrl ? (
                <img
                  src={resultDataUrl}
                  alt="retardified result"
                  className="w-full h-full object-cover"
                  data-testid="pfp-result-image"
                />
              ) : loading ? (
                <div className="flex flex-col items-center gap-3" data-testid="pfp-loading">
                  <Loader2 className="w-14 h-14 text-[#E8742C] animate-spin" />
                  <div className="font-display text-2xl md:text-3xl">cooking the retard…</div>
                  <div className="text-base text-[#1A1A1A]/70 text-center px-4">
                    10–25 seconds. propeller spinning at maximum rpm.
                  </div>
                </div>
              ) : (
                <div className="text-center px-6">
                  <Sparkles className="w-14 h-14 text-[#E8742C] mx-auto mb-2" />
                  <div className="font-display text-2xl md:text-3xl">awaiting upload</div>
                  <div className="text-base text-[#1A1A1A]/70">
                    your retardified pfp will appear here
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <button
                type="button"
                onClick={generate}
                disabled={!file || loading}
                className="crayon-btn disabled:opacity-50 disabled:cursor-not-allowed text-xl"
                data-testid="pfp-generate-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> cooking…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    {mode === "hat" ? "add propeller hat" : "retardify me"}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={download}
                disabled={!resultDataUrl}
                className="crayon-btn crayon-btn--green disabled:opacity-50 disabled:cursor-not-allowed text-xl"
                data-testid="pfp-download-button"
              >
                <Download className="w-5 h-5" /> download
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-sm md:text-base text-[#1A1A1A]/60 mt-6">
          we don't store your image. it goes straight to the ai and back.
          probably. we're retards, not lawyers.
        </p>
      </div>
    </section>
  );
};

export default PfpMaker;
